import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { InputText } from 'src/components/atoms/InputText';
import { InputTextType } from 'src/components/atoms/InputText/InputText';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { ResendRow } from './styles';
import { InvitationOtpFormProps, OtpFormValues } from './types';

const DEFAULT_COOLDOWN_SECONDS = 30;

const InvitationOtpForm: FC<InvitationOtpFormProps> = ({
  obfuscatedEmail,
  onVerify,
  onResend,
  isVerifying = false,
  inlineError,
  bannerError,
  resendCooldownSeconds = DEFAULT_COOLDOWN_SECONDS
}) => {
  // A code is always sent immediately before this form renders, so the resend
  // control starts in cooldown. The countdown is display-only state owned here;
  // the page supplies only the duration.
  const [secondsLeft, setSecondsLeft] = useState(resendCooldownSeconds);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    // Cleanup via return so React 18 strict-mode double-invoke and unmount both tear it down.
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const methods = useForm<OtpFormValues>({ mode: 'all' });
  const {
    formState: { isValid }
  } = methods;

  const handleResend = () => {
    onResend();
    setSecondsLeft(resendCooldownSeconds);
  };

  return (
    <>
      <Text color={TextColor.initial} variant={TextVariant.h3}>
        Enter your code
      </Text>
      <Gap height={1} />
      <Text color={TextColor.initial} variant={TextVariant.normal} isStretched>
        We sent a 6-digit code to {obfuscatedEmail}. Enter it below to continue.
      </Text>
      <Gap height={1.5} />
      {bannerError && (
        <>
          <Text color={TextColor.error} variant={TextVariant.small} isStretched>
            {bannerError}
          </Text>
          <Gap height={1} />
        </>
      )}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(({ otp }) => onVerify(otp))}>
          <InputText
            name="otp"
            type={InputTextType.text}
            placeholder="6-digit code"
            isDisabled={isVerifying}
            config={{
              required: 'Code is required',
              pattern: {
                value: /^\d{6}$/,
                message: 'Enter the 6-digit code'
              }
            }}
          />
          {inlineError && (
            <Text color={TextColor.error} variant={TextVariant.small}>
              {inlineError}
            </Text>
          )}
          <Gap height={1} />
          <Button
            name="VerifyInvitationOtp"
            variant={ButtonVariant.contained}
            color={ButtonColor.primary}
            size={ButtonSize.normal}
            type={ButtonType.submit}
            isDisabled={!isValid || isVerifying}
          >
            Verify
          </Button>
        </form>
      </FormProvider>
      <ResendRow>
        <Button
          name="ResendInvitationOtp"
          variant={ButtonVariant.ghost}
          color={ButtonColor.primary}
          size={ButtonSize.normal}
          isDisabled={secondsLeft > 0}
          onClick={handleResend}
        >
          {secondsLeft > 0 ? `Resend code in ${secondsLeft}s` : 'Resend code'}
        </Button>
      </ResendRow>
    </>
  );
};

export default InvitationOtpForm;
